<template>
  <div class="vx-row">
    <!-- Coluna principal -->
    <div class="vx-col lg:w-3/5 w-full">
      <vx-card class="mb-4">
        <h4 class="mb-1">{{ $t('cancellation.policy_data') }}</h4>
        <small class="text-muted d-block mb-3">{{ $t('cancellation.policy_data_hint') }}</small>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('cancellation.policy_name')" label-for="name">
              <b-form-input id="name" v-model="model.name" class="required" type="text"/>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('cancellation.establishments')" label-for="legal_entities">
              <v-select
                id="legal_entities"
                v-model="selectedLegalEntityIds"
                :options="legalEntityOptions"
                :reduce="opt => opt.id"
                label="name"
                :placeholder="$t('cancellation.establishments_placeholder')"
                multiple
                class="w-full required-select">
                <div slot="no-options">{{ $t('cancellation.no_establishments') }}</div>
              </v-select>
            </b-form-group>
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full">
            <b-form-group :label="$t('cancellation.applied_to')" label-for="applies_to">
              <v-select
                id="applies_to"
                v-model="appliesToItems"
                :options="productOptions"
                :disabled="model.is_default"
                :placeholder="model.is_default
                  ? $t('cancellation.applies_all')
                  : $t('cancellation.applies_placeholder')"
                multiple
                taggable
                push-tags
                class="w-full">
                <div slot="no-options">{{ $t('cancellation.no_products') }}</div>
              </v-select>
            </b-form-group>
          </div>
        </div>

        <div class="flex justify-between items-center border-top pt-3">
          <div>
            <div>{{ $t('cancellation.is_default') }}</div>
            <small class="text-muted">{{ $t('cancellation.is_default_hint') }}</small>
          </div>
          <b-form-checkbox v-model="model.is_default" switch/>
        </div>
      </vx-card>

      <!-- Faixas por antecedência -->
      <vx-card class="mb-4">
        <div class="flex justify-between items-center mb-4">
          <h4 class="mb-0">{{ $t('cancellation.tiers') }}</h4>
          <b-button variant="flat-primary" size="sm" @click="addTier">
            <feather-icon icon="PlusIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('cancellation.add_tier') }}</span>
          </b-button>
        </div>

        <div
          v-for="(tier, i) in version.tiers"
          :key="i"
          class="tier-row border rounded p-1 mb-1">
          <div class="vx-row items-end">
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('cancellation.antecedence')" label-size="sm">
                <b-form-input v-model.number="tier.min_hours" type="number" min="0" size="sm" placeholder="De (h)"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group label="Até (h)" label-size="sm">
                <b-form-input v-model.number="tier.max_hours" type="number" min="0" size="sm" placeholder="Sem limite"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('cancellation.remedy')" label-size="sm">
                <b-form-select v-model="tier.remedy" :options="remedyOptions" size="sm"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('cancellation.fixed_fee')" label-size="sm">
                <b-input-group prepend="R$" size="sm">
                  <b-form-input v-model.number="tier.fixed_fee" type="number" min="0" step="0.01" size="sm"/>
                </b-input-group>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('cancellation.penalty')" label-size="sm">
                <b-input-group append="%" size="sm">
                  <b-form-input v-model.number="tier.penalty_percent" type="number" min="0" max="100" size="sm"/>
                </b-input-group>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-1/12 w-full pb-2">
              <feather-icon
                icon="Trash2Icon"
                class="h-5 w-5 stroke-current text-danger cursor-pointer"
                @click="removeTier(i)"/>
            </div>
          </div>
        </div>
      </vx-card>

      <!-- Franquia e reagendamento -->
      <vx-card class="mb-4">
        <h4 class="mb-4">{{ $t('cancellation.franchise_title') }}</h4>

        <div class="flex justify-between items-center border-bottom pb-3 mb-3">
          <div>
            <div>{{ $t('cancellation.franchise_label') }}</div>
            <small class="text-muted">{{ $t('cancellation.franchise_hint') }}</small>
          </div>
          <div class="flex items-center gap-4">
            <b-form-input
              v-model.number="version.free_cancellations_per_month"
              type="number" min="0" style="width: 72px" class="text-center mr-2"/>
            <b-form-checkbox v-model="version.franchise_enabled" switch/>
          </div>
        </div>

        <div class="flex justify-between items-center border-bottom pb-3 mb-3">
          <div>
            <div>{{ $t('cancellation.allow_reschedule') }}</div>
            <small class="text-muted">
              {{ $t('cancellation.reschedule_hint', { hours: version.reschedule_min_hours }) }}
            </small>
          </div>
          <b-form-checkbox v-model="version.allow_reschedule" switch/>
        </div>

        <div class="flex justify-between items-center">
          <div>
            <div>{{ $t('cancellation.credit_no_slot') }}</div>
            <small class="text-muted">
              {{ $t('cancellation.credit_validity', { months: version.credit_validity_months }) }}
            </small>
          </div>
          <b-form-checkbox v-model="version.credit_when_no_slot" switch/>
        </div>
      </vx-card>

      <!-- Exceções com aprovação manual -->
      <vx-card class="mb-4">
        <h4 class="mb-1">{{ $t('cancellation.exceptions_title') }}</h4>
        <small class="text-muted d-block mb-3">{{ $t('cancellation.exceptions_hint') }}</small>

        <b-form-checkbox v-model="exceptions.medical_certificate" class="mb-1">
          {{ $t('cancellation.exception_.medical_certificate') }}
        </b-form-checkbox>
        <div class="flex items-center mb-1">
          <b-form-checkbox v-model="exceptions.high_value" class="mr-2">
            {{ $t('cancellation.exception_.high_value') }}
          </b-form-checkbox>
          <b-input-group prepend="R$" size="sm" style="width: 140px">
            <b-form-input
              v-model.number="exceptions.high_value_threshold"
              :disabled="!exceptions.high_value"
              type="number" min="0" size="sm"/>
          </b-input-group>
        </div>
        <b-form-checkbox v-model="exceptions.frequent_cancellations" class="mb-1">
          {{ $t('cancellation.exception_.frequent_cancellations') }}
        </b-form-checkbox>
        <b-form-checkbox v-model="exceptions.regret_window">
          {{ $t('cancellation.exception_.regret_window') }}
        </b-form-checkbox>
      </vx-card>
    </div>

    <!-- Coluna lateral -->
    <div class="vx-col lg:w-2/5 w-full">
      <vx-card class="mb-4">
        <h4 class="mb-3">{{ $t('cancellation.student_preview') }}</h4>
        <div class="border rounded p-1 mb-3">
          <small class="font-medium d-block mb-1">{{ $t('cancellation.student_preview_hint') }}</small>
          <small class="text-muted">{{ studentPreview }}</small>
        </div>
        <b-alert show variant="warning" class="p-1 mb-3">
          <div class="flex items-start">
            <feather-icon icon="GitBranchIcon" class="mr-1 flex-shrink-0"/>
            <small>{{ $t('cancellation.publish_warning', { version: 'v' + version.version }) }}</small>
          </div>
        </b-alert>
        <div class="flex gap-4">
          <b-button variant="primary" class="mr-2" :disabled="!model.name" @click="publish">
            {{ $t('cancellation.publish') }} v{{ version.version }}
          </b-button>
          <b-button variant="outline-secondary" :disabled="!model.name" @click="saveDraft">
            {{ $t('cancellation.save_draft') }}
          </b-button>
        </div>
      </vx-card>

      <vx-card>
        <h4 class="mb-3">{{ $t('cancellation.simulator') }}</h4>
        <b-form-group :label="$t('cancellation.simulator_at')">
          <b-form-select v-model="simulator.hours" :options="hourOptions" @change="simulate"/>
        </b-form-group>
        <b-form-group label="Valor da reserva">
          <b-input-group prepend="R$">
            <b-form-input v-model.number="simulator.amount" type="number" min="0" @change="simulate"/>
          </b-input-group>
        </b-form-group>

        <div v-if="simulator.result" class="border-top pt-3">
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.simulator_tier') }}</small>
            <small>{{ simulator.result.tier_label || '—' }}</small>
          </div>
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.simulator_receives') }}</small>
            <small>
              {{ formatValue(simulator.result.remedy_amount) }}
              <span v-if="simulator.result.remedy === 'CR'">em crédito</span>
              <span v-else-if="simulator.result.remedy === 'CH'">· cliente escolhe</span>
            </small>
          </div>
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.simulator_retains') }}</small>
            <small>{{ formatValue(simulator.result.retained_amount) }}</small>
          </div>
          <div class="flex justify-between">
            <small class="text-muted">{{ $t('cancellation.simulator_resolution') }}</small>
            <small
              :class="simulator.result.resolution === 'AU' ? 'text-success' : 'text-warning'"
              class="font-medium">
              {{ $t('cancellation.resolution_.' + simulator.result.resolution) }}
            </small>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { currencyFormatter } from '@/util/Util'

import CancellationPolicyService from '@/services/api/CancellationPolicyService'
import CancellationPolicyVersionService from '@/services/api/CancellationPolicyVersionService'
import ProductService from '@/services/api/ProductService'
import LegalEntityService from '@/services/api/LegalEntityService'

export default {
  components: {
    BAlert,
    BButton,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    vSelect
  },

  props: {
    id: {
      type: Number,
      default: null
    }
  },

  data: () => ({
    service: null,
    versionService: null,
    productService: null,
    legalEntityService: null,
    productOptions: [],
    legalEntityOptions: [],
    selectedLegalEntityIds: [],
    appliesToItems: [],
    model: {
      id: null,
      name: '',
      applies_to: '',
      is_default: false
    },
    version: {
      id: null,
      version: 1,
      franchise_enabled: true,
      free_cancellations_per_month: 3,
      allow_reschedule: true,
      reschedule_limit: 1,
      reschedule_min_hours: 12,
      reschedule_same_modality: true,
      credit_when_no_slot: true,
      credit_validity_months: 12,
      tiers: [
        { min_hours: 24, max_hours: null, remedy: 'FR', fixed_fee: 0, penalty_percent: 0 },
        { min_hours: 12, max_hours: 24, remedy: 'CH', fixed_fee: 0, penalty_percent: 10 },
        { min_hours: null, max_hours: 12, remedy: 'NR', fixed_fee: 0, penalty_percent: 100 }
      ]
    },
    exceptions: {
      medical_certificate: false,
      high_value: false,
      high_value_threshold: 300,
      frequent_cancellations: false,
      regret_window: false
    },
    simulator: {
      hours: 18,
      amount: 60,
      result: null
    }
  }),

  computed: {
    remedyOptions() {
      return ['FR', 'CH', 'CR', 'NR', 'PR'].map(value => ({
        value,
        text: this.$t('cancellation.remedy_.' + value)
      }))
    },
    hourOptions() {
      return [2, 6, 10, 14, 18, 22, 30, 48].map(hours => ({
        value: hours,
        text: this.$t('cancellation.simulator_hours', { hours })
      }))
    },
    studentPreview() {
      const parts = []
      const sorted = [...this.version.tiers].sort((a, b) => (b.min_hours || 0) - (a.min_hours || 0))
      sorted.forEach(tier => {
        const range = this.tierRangeText(tier)
        switch (tier.remedy) {
          case 'FR':
            parts.push(`Cancele ${range} e receba o valor integral de volta.`)
            break
          case 'CH':
            parts.push(`${this.capitalize(range)} você escolhe entre crédito na carteira ou reembolso com ${tier.penalty_percent || 0}% de taxa.`)
            break
          case 'CR':
            parts.push(`${this.capitalize(range)} a devolução é em crédito na carteira.`)
            break
          case 'NR':
            parts.push(`${this.capitalize(range)} não há devolução.`)
            break
          case 'PR':
            parts.push(`${this.capitalize(range)} a devolução é proporcional ao período não usado.`)
            break
        }
      })
      if (this.version.franchise_enabled) {
        parts.push(`Você tem ${this.version.free_cancellations_per_month} cancelamentos sem taxa por mês`)
      }
      if (this.version.allow_reschedule) {
        parts.push(`${this.version.franchise_enabled ? 'e' : 'Você'} pode reagendar ${this.version.reschedule_limit === 1 ? 'uma vez' : this.version.reschedule_limit + ' vezes'} por reserva.`)
      }
      return parts.join(' ')
    }
  },

  methods: {
    tierRangeText(tier) {
      const min = tier.min_hours
      const max = tier.max_hours
      if (min && !max) return `até ${min}h antes`
      if (min && max) return `entre ${min}h e ${max}h`
      return `com menos de ${max}h`
    },
    capitalize(text) {
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    formatValue(value) {
      return currencyFormatter(value || 0)
    },
    addTier() {
      this.version.tiers.push({ min_hours: null, max_hours: null, remedy: 'FR', fixed_fee: 0, penalty_percent: 0 })
    },
    removeTier(i) {
      this.version.tiers.splice(i, 1)
    },
    versionPayload() {
      return {
        franchise_enabled: this.version.franchise_enabled,
        free_cancellations_per_month: this.version.free_cancellations_per_month,
        allow_reschedule: this.version.allow_reschedule,
        reschedule_limit: this.version.reschedule_limit,
        reschedule_min_hours: this.version.reschedule_min_hours,
        reschedule_same_modality: this.version.reschedule_same_modality,
        credit_when_no_slot: this.version.credit_when_no_slot,
        credit_validity_months: this.version.credit_validity_months,
        manual_exceptions: { ...this.exceptions },
        tiers: this.version.tiers.map(tier => ({
          min_hours: tier.min_hours === '' ? null : tier.min_hours,
          max_hours: tier.max_hours === '' ? null : tier.max_hours,
          remedy: tier.remedy,
          fixed_fee: tier.fixed_fee || 0,
          penalty_percent: tier.penalty_percent || 0
        }))
      }
    },
    loadProducts() {
      this.productService.grid('', { page: 1, per_page: 100 }).then(response => {
        this.productOptions = this._.get(response, 'model.data', []).map(product => product.name)
      })
    },
    loadLegalEntities() {
      this.legalEntityService.grid('', { page: 1, per_page: 100 }).then(response => {
        this.legalEntityOptions = this._.get(response, 'model.data', []).map(entity => ({
          id: entity.id,
          name: entity.trading_name
        }))
      })
    },
    savePolicy() {
      const data = {
        id: this.model.id,
        name: this.model.name,
        applies_to: this.model.is_default
          ? this.$t('cancellation.applies_all')
          : this.appliesToItems.join(', '),
        is_default: this.model.is_default,
        legal_entity_ids: this.selectedLegalEntityIds
      }
      return this.service.createOrUpdate(data).then(policy => {
        this.model.id = policy.id
        return policy
      })
    },
    saveDraft() {
      this.$vs.loading()
      this.savePolicy()
        .then(() => this.service.saveVersion(this.model.id, this.versionPayload()))
        .then(draft => {
          this.version.id = draft.id
          this.version.version = draft.version
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          if (!this.id) {
            this.$router.push(`/cancellations/policies/${this.model.id}/edit`)
          }
        })
        .catch(() => {})
        .finally(() => { this.$vs.loading.close() })
    },
    publish() {
      this.$vs.loading()
      this.savePolicy()
        .then(() => this.service.saveVersion(this.model.id, this.versionPayload()))
        .then(draft => this.versionService.publish(draft.id))
        .then(published => {
          this.version.id = null
          this.version.version = published.version + 1
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.$router.push('/cancellations/policies')
        })
        .catch(() => {})
        .finally(() => { this.$vs.loading.close() })
    },
    simulate() {
      const payload = {
        config: this.versionPayload(),
        antecedence_hours: this.simulator.hours,
        amount: this.simulator.amount
      }
      this.service.simulate(payload).then(
        result => { this.simulator.result = result },
        () => {}
      )
    },
    loadData(id) {
      this.$vs.loading()
      Promise.all([
        this.service.read(id),
        this.service.getVersions(id)
      ]).then(([policy, versions]) => {
        this.model.id = policy.id
        this.model.name = policy.name
        this.model.applies_to = policy.applies_to
        this.model.is_default = !!policy.is_default
        this.appliesToItems = policy.applies_to && !policy.is_default
          ? policy.applies_to.split(',').map(item => item.trim()).filter(Boolean)
          : []
        this.selectedLegalEntityIds = (policy.legal_entities || []).map(entity => entity.id)

        // Edita o rascunho se existir; senão parte da vigente para a próxima versão
        const draft = versions.find(version => version.status === 'DR')
        const active = versions.find(version => version.status === 'AC')
        const base = draft || active

        if (base) {
          this.version.id = draft ? draft.id : null
          this.version.version = draft ? draft.version : ((active ? active.version : 0) + 1)
          this.version.franchise_enabled = !!base.franchise_enabled
          this.version.free_cancellations_per_month = base.free_cancellations_per_month
          this.version.allow_reschedule = !!base.allow_reschedule
          this.version.reschedule_limit = base.reschedule_limit
          this.version.reschedule_min_hours = base.reschedule_min_hours
          this.version.reschedule_same_modality = !!base.reschedule_same_modality
          this.version.credit_when_no_slot = !!base.credit_when_no_slot
          this.version.credit_validity_months = base.credit_validity_months
          this.version.tiers = (base.tiers || []).map(tier => ({
            min_hours: tier.min_hours,
            max_hours: tier.max_hours,
            remedy: tier.remedy,
            fixed_fee: tier.fixed_fee,
            penalty_percent: tier.penalty_percent
          }))
          this.exceptions = {
            medical_certificate: false,
            high_value: false,
            high_value_threshold: 300,
            frequent_cancellations: false,
            regret_window: false,
            ...(base.manual_exceptions || {})
          }
        }
        this.simulate()
      }).finally(() => { this.$vs.loading.close() })
    }
  },

  beforeMount() {
    this.service = CancellationPolicyService.build(this.$vs)
    this.versionService = CancellationPolicyVersionService.build(this.$vs)
    this.productService = ProductService.build(this.$vs)
    this.legalEntityService = LegalEntityService.build(this.$vs)
  },

  mounted() {
    this.loadProducts()
    this.loadLegalEntities()
    if (this.id) {
      this.loadData(this.id)
    } else {
      this.simulate()
    }
  }
}
</script>

<style lang="scss" scoped>
.tier-row {
  border: 1px solid #e4e4e4;
}
</style>
